import React, { useState, useEffect } from 'react';

const COLORS = ['Red', 'Blue', 'Green', 'Yellow'];
const TOTAL_TRIALS = 30;
const RESPONSE_WINDOW = 3000;
const FIXATION_DURATION = 750;

const generateTrials = () => {
  const trials = [];

  for (let i = 0; i < TOTAL_TRIALS / 2; i++) {
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    trials.push({ word: color, fontColor: color, congruent: true });
  }

  for (let i = 0; i < TOTAL_TRIALS / 2; i++) {
    let word = COLORS[Math.floor(Math.random() * COLORS.length)];
    let fontColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    while (fontColor === word) {
      fontColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    }
    trials.push({ word, fontColor, congruent: false });
  }

  return trials.sort(() => Math.random() - 0.5);
};

export default function Stroop({ onComplete }) {
  const [phase, setPhase] = useState('instructions');
  const [countdown, setCountdown] = useState(3);
  const [trials] = useState(generateTrials());
  const [index, setIndex] = useState(0);
  const [trialPhase, setTrialPhase] = useState('fixation');
  const [stimulusTime, setStimulusTime] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [responses, setResponses] = useState([]);

  const currentTrial = trials[index];

  useEffect(() => {
    if (phase === 'countdown') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setPhase('task');
        setTrialPhase('fixation');
      }
    }
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'task' || index >= trials.length) return;

    if (trialPhase === 'fixation') {
      const timer = setTimeout(() => {
        setStimulusTime(Date.now());
        setClicked(false);
        setTrialPhase('stimulus');
      }, FIXATION_DURATION);
      return () => clearTimeout(timer);
    }

    if (trialPhase === 'stimulus') {
      const timer = setTimeout(() => {
        setClicked(true); // ✅ Fix: Ensure we block further clicks
        recordResponse(null); // no response
      }, RESPONSE_WINDOW);
      return () => clearTimeout(timer);
    }
  }, [phase, index, trialPhase]);

  const recordResponse = (choice) => {
    const rt = choice ? Date.now() - stimulusTime : null;
    const correct = choice === currentTrial.fontColor;

    const trialData = {
      trial: index + 1,
      word: currentTrial.word,
      fontColor: currentTrial.fontColor,
      congruent: currentTrial.congruent,
      userChoice: choice,
      correct,
      reactionTime: rt,
    };

    const allResponses = [...responses, trialData];

    if (index + 1 < trials.length) {
      setResponses(allResponses);
      setIndex(i => i + 1);
      setTrialPhase('fixation');
    } else {
      const correctCount = allResponses.filter(r => r.correct).length;
      const rtValues = allResponses
        .filter(r => r.reactionTime !== null)
        .map(r => r.reactionTime);
      const avgRT = rtValues.length
        ? rtValues.reduce((a, b) => a + b, 0) / rtValues.length
        : null;

      setPhase('done');

      if (onComplete) {
        onComplete({
          correct: correctCount,
          totalTrials: allResponses.length,
          avgRT,
        });
      }
    }
  };

  const handleClick = (color) => {
    if (!clicked) {
      setClicked(true);
      recordResponse(color);
    }
  };

  if (phase === 'done') {
    const correct = responses.filter(r => r.correct).length;
    const congruentRT = responses
      .filter(r => r.correct && r.congruent && r.reactionTime !== null)
      .map(r => r.reactionTime);
    const incongruentRT = responses
      .filter(r => r.correct && !r.congruent && r.reactionTime !== null)
      .map(r => r.reactionTime);

    const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Stroop Task Complete</h2>
        <p>Correct: {correct} / {TOTAL_TRIALS}</p>
        <p>Avg RT (Congruent): {Math.round(avg(congruentRT))} ms</p>
        <p>Avg RT (Incongruent): {Math.round(avg(incongruentRT))} ms</p>
      </div>
    );
  }

  if (phase === 'instructions') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Welcome to the Stroop Task</h2>
        <p>You will see color words (e.g., "Red") shown in different font colors.</p>
        <p>Your task is to click the button matching the FONT COLOR — not the word.</p>
        <p>Click as fast and accurately as possible.</p>
        <button onClick={() => setPhase('countdown')}>Start</button>
      </div>
    );
  }

  if (phase === 'countdown') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Get Ready</h2>
        <p>Starting in... {countdown}</p>
      </div>
    );
  }

  if (phase === 'task' && trialPhase === 'fixation') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <div style={{ fontSize: '5rem' }}>+</div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '3rem' }}>
      <h2>Trial {index + 1}</h2>
      <div
        style={{
          fontSize: '4rem',
          color: currentTrial.fontColor.toLowerCase(),
          margin: '1rem',
        }}
      >
        {currentTrial.word}
      </div>
      <p>Click the button that matches the FONT COLOR</p>
      {COLORS.map((color) => (
        <button
          key={color}
          onClick={() => handleClick(color)}
          style={{
            margin: '0.5rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: color.toLowerCase(),
            color: 'white',
            border: 'none',
            fontSize: '1rem',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
          disabled={clicked}
        >
          {color}
        </button>
      ))}
    </div>
  );
}
